import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { sessionStorageService, AUTH_ROUTES } from "../features/authentication";
import { AI_SEARCH_ROUTE, TALK_TO_ME_ENTRY_POINT } from "./ai/Constants";

import "../styles/TalkToMeWidget.css";

const ROBOT_IMG = "/images/ai-robot.png";
const PILL_LABEL = "Talk to Me";

// Reveal/breathe timings carried over verbatim from the property-page pill
// this replaces (pages/AskDiscoverWidget.jsx), so the motion reads exactly
// the same: fade in at 800ms, first expand at 1800ms, then 4s expanded /
// 3.5s collapsed forever.
const REVEAL_DELAY_MS = 800;
const FIRST_EXPAND_DELAY_MS = 1800;
const EXPANDED_MS = 4000;
const COLLAPSED_MS = 3500;

/**
 * Floating "Talk to Me" pill on the homepage: the single entry point into
 * the AI property-shortlisting experience.
 *
 * Deliberately tiny. It owns no chat, no conversation id and no questions
 * of its own -- clicking it authenticates (using the app's existing
 * redirectTo convention) and then navigates to /ai-search, which remains
 * the source of truth for the chat session, the chat history and the
 * six-question guided onboarding. It carries an explicit `entryPoint` in
 * the navigation state purely so AiSearchPage knows to open with the
 * guided introduction rather than an empty chat.
 *
 * It is general, never property-aware: no property object, no property
 * mini-card, no property-derived chat id.
 */
export default function TalkToMeWidget() {
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const cycleTimerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const revealTimer = setTimeout(() => setVisible(true), REVEAL_DELAY_MS);

        const cycle = () => {
            setExpanded(true);
            cycleTimerRef.current = setTimeout(() => {
                setExpanded(false);
                cycleTimerRef.current = setTimeout(cycle, COLLAPSED_MS);
            }, EXPANDED_MS);
        };
        cycleTimerRef.current = setTimeout(cycle, FIRST_EXPAND_DELAY_MS);

        return () => {
            clearTimeout(revealTimer);
            clearTimeout(cycleTimerRef.current);
        };
    }, []);

    const enterAiSearch = useCallback(() => {
        // Same gate the rest of the app uses: no session -> /auth with the
        // return target in location.state, which AuthenticationPage replays
        // on success. Nothing new is invented here.
        if (!sessionStorageService.get()) {
            navigate(AUTH_ROUTES.LOGIN, {
                state: { redirectTo: AI_SEARCH_ROUTE, entryPoint: TALK_TO_ME_ENTRY_POINT },
            });
            return;
        }
        navigate(AI_SEARCH_ROUTE, { state: { entryPoint: TALK_TO_ME_ENTRY_POINT } });
    }, [navigate]);

    const pill = (
        <div
            className={`ttm-pill-trigger ${visible ? "ttm-pill-visible" : ""}`}
            onClick={enterAiSearch}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    enterAiSearch();
                }
            }}
            role="button"
            tabIndex={0}
            aria-label={PILL_LABEL}
        >
            <div className={`ttm-pill-text ${expanded ? "ttm-pill-text-expanded" : ""}`}>
                <span className="ttm-pill-label">{PILL_LABEL}</span>
            </div>
            <div className="ttm-pill-robot-wrap">
                <img src={ROBOT_IMG} alt="" className="ttm-pill-robot" />
                <span className="ttm-online-dot" />
            </div>
        </div>
    );

    return createPortal(pill, document.body);
}
